import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Users, AlertCircle, CalendarClock } from "lucide-react";

const STATUS_ORDER = ["契約中", "契約予定", "休止中", "一時中断", "入院中", "契約終了", "未設定"];
const COLOR = {
  "契約中": "bg-[#2C5545] text-white",
  "契約予定": "bg-[#4A6B8C] text-white",
  "休止中": "bg-[#D4A050] text-white",
  "一時中断": "bg-[#D4A050] text-white",
  "入院中": "bg-[#A74141] text-white",
  "契約終了": "bg-[#8A8A86] text-white",
  "未設定": "bg-[#E5E5E3] text-[#1C1C1A]",
};

export default function ContractStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard/contract-stats")
      .then((r) => setData(r.data))
      .catch(() => setData({ counts: {}, total: 0, ending_soon: [] }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="p-6 border-[#E5E5E3] bg-white" data-testid="contract-stats-card">
        <div className="text-sm text-[#8A8A86]">契約状況を読み込み中…</div>
      </Card>
    );
  }

  const counts = data?.counts || {};
  const total = data?.total || 0;
  const endingSoon = data?.ending_soon || [];

  return (
    <Card className="p-6 border-[#E5E5E3] bg-white" data-testid="contract-stats-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#2C5545]/10 flex items-center justify-center">
            <Users size={18} className="text-[#2C5545]" strokeWidth={1.5} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#1C1C1A]">契約状況 集計</h2>
            <div className="text-xs text-[#8A8A86] font-numeric tracking-wider">
              利用者全{total}名 / 契約終了30日以内 {data?.ending_soon_total || 0}名
            </div>
          </div>
        </div>
        <button
          data-testid="manage-patients-button"
          onClick={() => nav("/patients")}
          className="text-sm text-[#2C5545] hover:underline"
        >
          利用者一覧 →
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-5">
        {STATUS_ORDER.filter((s) => counts[s] !== undefined).map((s) => (
          <button
            type="button"
            key={s}
            data-testid={`contract-count-${s}`}
            onClick={() => nav(`/patients?status=${encodeURIComponent(s)}`)}
            className="text-left p-2.5 rounded-lg border border-[#E5E5E3] hover:border-[#2C5545]/40 hover:bg-[#FBFBF9] transition"
          >
            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium mb-1 ${COLOR[s] || "bg-[#E5E5E3] text-[#1C1C1A]"}`}>
              {s}
            </span>
            <div className="text-2xl font-bold font-numeric text-[#1C1C1A]">{counts[s]}</div>
            <div className="text-[10px] text-[#8A8A86]">名</div>
          </button>
        ))}
      </div>

      {endingSoon.length > 0 && (
        <div className="border-t border-dashed border-[#A74141]/30 pt-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle size={14} className="text-[#A74141]" />
            <span className="text-sm font-bold text-[#A74141]">契約終了が近い利用者 (30日以内)</span>
          </div>
          <div className="space-y-1">
            {endingSoon.map((p) => (
              <div
                key={p.id}
                data-testid={`ending-soon-${p.id}`}
                onClick={() => nav(`/patients/${p.id}`)}
                className="flex items-center gap-3 p-2 rounded-lg bg-[#A74141]/5 border border-[#A74141]/20 cursor-pointer hover:bg-[#A74141]/10 transition"
              >
                <CalendarClock size={14} className="text-[#A74141] shrink-0" />
                <span className="font-medium text-sm flex-1 truncate">{p.name_kanji}</span>
                {p.contract_status && (
                  <span className={`px-2 py-0.5 rounded-full text-[10px] ${COLOR[p.contract_status] || "bg-[#E5E5E3]"}`}>
                    {p.contract_status}
                  </span>
                )}
                <span className="text-xs font-numeric text-[#A74141]">
                  {p.contract_end_date}
                  <span className="ml-1 text-[10px]">(あと{p.days_left}日)</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
